import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import CompositeFilter, { ANALYSIS_FILTERS, getActiveFilters } from 'components/charts/CompositeFilter';
import DataCards from 'components/charts/DataCards';
import DataListingCard from 'components/charts/DataListingCard';
import DistributionList from 'components/charts/DistributionList';
import StatisticsDataCards from 'components/charts/StatisticsDataCards';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import DoughnutChart from 'components/charts/chartJS/DoughnutChart';
import { chartColors } from 'components/charts/chartJS/utils';
import { ComparisonFilters, Divider, FiltersContainer } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import Authorization from 'components/global/Authorization';
import Card from 'components/global/Card';
import ManageWidgetsModal from 'components/global/ManageWidgets/ManageWidgetsModal';
import useDefaultFiltersSync from 'components/global/ManageWidgets/useDefaultFiltersSync';
import { calcultateCustomWidgetDateFilter } from 'components/global/ManageWidgets/widgetsUtils';
import PageHeader from 'components/global/PageHeader';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import useAccessControl from 'hooks/useAccessControl';
import useBrandManagerFilter from 'hooks/useBrandManagerFilter';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { chartsConfig } from 'pages/Analysis/analysisWidgetsConfig';
import { getModulesList } from 'pages/OTOModules/OTOPackageListSlice';
import { routeMapping } from 'pages/Shipments/utils/shipmentStatusMapping';

import { REPORT } from 'services/consts';

import PATHS from 'utils/paths';

import {
	faBoxesStacked,
	faExpand,
	faFire,
	faHandHoldingBox,
	faSackDollar,
	faSquareCheck,
	faTruckClock,
	faTruckFast,
	faWallet
} from '@fortawesome/pro-regular-svg-icons';
import { faObjectsColumn } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Dashboard.styled';
import { dashboardChartsConfigRaw } from './dashboardConfig';

const Dashboard = () => {
	const data = chartsConfig(dashboardChartsConfigRaw);
	const navigate = useNavigate();
	const { userType, assignedBrands } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	return (
		<div>
			<PageHeader
				title="dashboard"
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/dashboard-icon-new.json.gz">
				{!isBrandManager && (
					<Authorization path="/dashboard/monitor-view">
						<Button
							icon={faExpand}
							label={'dashboard.monitorView'}
							variant="blackOutline"
							onClick={() => navigate(PATHS.DASHBOARD_MONITOR_VIEW)}
						/>
					</Authorization>
				)}
				<Button
					icon={faObjectsColumn}
					label={'manageWidgets.buttonLabel'}
					variant="blue"
					onClick={() =>
						NiceModal.show(ManageWidgetsModal, {
							data
						})
					}
				/>
			</PageHeader>
			<WidgetPageContainer>
				<WidgetWrapper widgetId={'numberOfOrders'}>
					<NumberOfOrders isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
				</WidgetWrapper>
				<WidgetWrapper widgetId={'overview'}>
					<Divider />
					<Overview isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
				</WidgetWrapper>
				<WidgetWrapper widgetId={'closedAndProcessingOrders'}>
					<Divider />
					<ClosedAndProcessingOrders isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
				</WidgetWrapper>
				<WidgetWrapper widgetId={'currentOrders'}>
					<Divider />
					<CurrentOrders isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
				</WidgetWrapper>
				{!isBrandManager && (
					<WidgetWrapper widgetId={'dashboard.orderDistribution'}>
						<Divider />
						<OrderDistribution />
					</WidgetWrapper>
				)}
			</WidgetPageContainer>
		</div>
	);
};

const filtersList = [
	ANALYSIS_FILTERS.destinationCountry,
	ANALYSIS_FILTERS.destinationCity,
	ANALYSIS_FILTERS.warehouse,
	ANALYSIS_FILTERS.branch,
	ANALYSIS_FILTERS.brand,
	ANALYSIS_FILTERS.salesChannel,
	ANALYSIS_FILTERS.paymentMethod
];

export const NumberOfOrders = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			filter: getActiveFilters(extraFilters),
			from: filter.start,
			to: filter.end
		}),
		[filter, extraFilters]
	);

	useDefaultFiltersSync({
		defaultFilters,
		filter,
		setFilter,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_SHIPMENT_NUMBERS,
		body
	});

	const navigate = useNavigate();

	const handleButtonClick = (cardName, Header, accessor, extraFilters) => {
		navigate(routeMapping[cardName], {
			state: {
				defaultFilters: [
					{
						column: {
							Header,
							accessor,
							filterType: FilterType.date
						},
						action: FilterAction.between,
						...(body && {
							value: {
								start: body.from,
								end: body.to
							}
						})
					},
					...(extraFilters || [])
				]
			}
		});
	};

	const isAuthorized = useAccessControl();

	const cardsConfig = [
		{
			accessor: 'pending',
			label: 'pending',
			customIcon: 'fa-regular-box-clock',
			color: '#FF9500',
			backgroundColor: '#FF950014',
			...(isAuthorized('/shipments/pending-orders') && {
				onButtonClick: () => {
					handleButtonClick('pending', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'awaitingPickup',
			label: 'awaitingPickup',
			icon: faTruckClock,
			color: '#AB47BC',
			backgroundColor: '#AB47BC14',
			...(isAuthorized('/shipments/awaiting-pickup') && {
				onButtonClick: () => {
					handleButtonClick('awaitingPickup', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'currentlyShipping',
			label: 'currentlyShipping',
			icon: faTruckFast,
			color: '#256CF0',
			backgroundColor: '#256CF014',
			...(isAuthorized('/shipments/currently-shipping') && {
				onButtonClick: () => {
					handleButtonClick('currentlyShipping', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'shipmentOnHold',
			label: 'shipmentOnHold',
			icon: faHandHoldingBox,
			color: '#15AFD5',
			backgroundColor: '#15AFD514',
			...(isAuthorized('/shipments/onhold-orders') && {
				onButtonClick: () => {
					handleButtonClick('shipmentOnHold', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			customIcon: 'fa-regular-box-circle-check',
			color: '#55D491',
			backgroundColor: '#55D49114',
			...(isAuthorized('/shipments/delivered-orders') && {
				onButtonClick: () => {
					handleButtonClick('delivered', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			color: '#FFBA08',
			backgroundColor: '#FFBA0814',
			...(isAuthorized('/shipments/returned-orders') && {
				onButtonClick: () => {
					handleButtonClick('returned', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'canceled',
			label: 'canceled',
			customIcon: 'fa-regular-box-circle-xmark',
			color: '#F3372A',
			backgroundColor: '#F3372A14',
			...(isAuthorized('/shipments/canceled-orders') && {
				onButtonClick: () => {
					handleButtonClick('canceled', 'orderDate', 'createdDate');
				}
			})
		},
		{
			accessor: 'allOrders',
			label: 'allOrders',
			icon: faBoxesStacked,
			color: '#79C736',
			backgroundColor: '#79C73614',
			...(isAuthorized('/shipments/all-orders') && {
				onButtonClick: () => {
					handleButtonClick('allOrders', 'orderDate', 'createdDate');
				}
			})
		}
	];

	return (
		<div ref={ref}>
			<ChartHeader
				title={widgetName || 'numberOfOrders'}
				filter={
					<FiltersContainer>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							size="small"
							className="py-0"
							label="filters"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilter}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
					</FiltersContainer>
				}
				showDivider={false}
				subtitle={'dashboard.numberOfOrdersSubtitle'}
				gutterBottom
			/>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

export const Overview = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filterOne, setFilterOne] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const [filterTwo, setFilterTwo] = useState({
		start: dayjs().subtract(59, 'day').startOf('day').toISOString(),
		end: dayjs().subtract(30, 'day').endOf('day').toISOString()
	});

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			fromStart: filterOne.start,
			fromEnd: filterOne.end,
			toStart: filterTwo.start,
			toEnd: filterTwo.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filterOne, filterTwo, extraFilters]
	);

	useDefaultFiltersSync({
		defaultFilters,
		filter: filterOne,
		setFilter: setFilterOne,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const dataCardConfigs = [
		{
			accessor: 'orderQuantity',
			icon: faFire,
			label: 'dashboard.statistic.newOrders'
		},
		{
			accessor: 'CODQuantity',
			icon: faWallet,
			label: 'dashboard.statistic.numberOfCodOrders'
		},
		{
			accessor: 'deliveryQuantity',
			icon: faSquareCheck,
			label: 'dashboard.statistic.numberOfDeliveries'
		},
		{
			accessor: 'orderAmount',
			customIcon: 'fa-regular-cart-shopping-circle-dollar',
			label: 'dashboard.statistic.totalOrderAmount'
		},
		{
			accessor: 'CODAmount',
			icon: faSackDollar,
			label: 'dashboard.statistic.totalCodAmount'
		},
		{
			accessor: 'deliveryAmount',
			customIcon: 'fa-regular-building-columns-circle-dollar',
			label: 'dashboard.statistic.totalDeliveryAmount'
		}
	];

	return (
		<>
			<ChartHeader
				title={widgetName || 'overview'}
				filter={
					<ComparisonFilters>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							size="small"
							className="py-0"
							label="filters"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilterOne}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
						<Typography variant="subtitle1">dashboard.compareWithLabel</Typography>
						<DateFilter defaultDay={'customDateRange'} setValue={setFilterTwo} defaultFilter={filterTwo} />
					</ComparisonFilters>
				}
				showDivider={false}
				subtitle={'dashboard.overviewSubtitle'}
				gutterBottom
			/>
			<StatisticsDataCards
				body={body}
				fetchURL={REPORT.DASHBOARD.GET_DASHBOARD_OVERVIEW}
				cardConfigs={dataCardConfigs}
			/>
		</>
	);
};

export const CurrentOrders = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			from: filter.start,
			to: filter.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filter, extraFilters]
	);

	useDefaultFiltersSync({
		defaultFilters,
		filter,
		setFilter,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	return (
		<div>
			<ChartHeader
				title={widgetName || 'processingOrders'}
				subtitle={'dashboard.processingOrdersSubtitle'}
				filter={
					<FiltersContainer>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							size="small"
							className="py-0"
							label="filters"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilter}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
					</FiltersContainer>
				}
				showDivider={false}
				gutterBottom
			/>
			<S.ThreeCardsInRow>
				<PendingPreparation requestBody={body} />
				<PendingPickup requestBody={body} />
				<PendingDelivery requestBody={body} />
			</S.ThreeCardsInRow>
		</div>
	);
};

const parseData = (dataInput, t) => ({
	labels: dataInput.map((item) => t(item.label)),
	datasets: [
		{
			data: dataInput.map((item) => parseInt(item.data)),
			backgroundColor: chartColors.backgroundColor,
			borderColor: 'white'
		}
	]
});

const PendingPreparation = ({ requestBody }) => {
	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_PREPARATION_ORDERS,
		parseData: (data) => parseData(data, t),
		body: requestBody
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'pendingPreparation'} />
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

const PendingPickup = ({ requestBody }) => {
	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_PICKUP_ORDERS,
		parseData: (data) => parseData(data, t),
		body: requestBody
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'pendingPickup'} />
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

export const PendingDelivery = ({ requestBody }) => {
	const { t } = useTranslation();
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CURRENT_DELIVERY_ORDERS,
		parseData: (data) => parseData(data, t),
		body: requestBody
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={'pendingDelivery'} />
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

const afterFetch = (data, t) => ({
	labels: data.map((item) => t(item.label)),
	datasets: [
		{
			data: data.map((item) => item.value || 0),
			backgroundColor: chartColors.backgroundColor,
			borderColor: 'white'
		}
	]
});

export const ClosedAndProcessingOrders = ({ isBrandManager, assignedBrands }) => {
	return (
		<div>
			<ChartHeader
				title={'closedAndProcessingOrders'}
				subtitle={'dashboard.closedAndProcessingOrdersSubtitle'}
				showDivider={false}
				gutterBottom
			/>
			<S.TwoCardsInRow>
				<ClosedOrders isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
				<ProcessingOrders isBrandManager={isBrandManager} assignedBrands={assignedBrands} />
			</S.TwoCardsInRow>
		</div>
	);
};

export const ClosedOrders = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const { t } = useTranslation();
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			from: filter.start,
			to: filter.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filter, extraFilters]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_CLOSED_ORDERS,
		body,
		parseData: (data) => afterFetch(data, t)
	});

	useDefaultFiltersSync({
		defaultFilters,
		filter,
		setFilter,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'closedOrders'}
				filter={
					<FiltersContainer>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							size="small"
							className="py-0"
							label="filters"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilter}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
					</FiltersContainer>
				}
			/>
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

export const ProcessingOrders = ({ widgetName, onApplyFilters, defaultFilters, isBrandManager, assignedBrands }) => {
	const { t } = useTranslation();
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			from: filter.start,
			to: filter.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filter, extraFilters]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_PROCESSING_ORDERS,
		body,
		parseData: (data) => afterFetch(data, t)
	});

	useDefaultFiltersSync({
		defaultFilters,
		filter,
		setFilter,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'processingOrders'}
				filter={
					<FiltersContainer>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
							size="small"
							className="py-0"
							label="filters"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilter}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
					</FiltersContainer>
				}
			/>
			<DoughnutChart data={data} innerTitle={'totalOrders'} loading={loading} />
		</Card>
	);
};

export const OrderDistribution = ({ widgetName }) => {
	const dispatch = useDispatch();
	const { modulesList } = useSelector((state) => state.otoPackageList);
	useEffect(() => {
		dispatch(getModulesList());
	}, []);

	const isBranchModuleActive = useMemo(() => {
		const branchModule = modulesList
			?.find((pack) => {
				return pack.name === 'EnterprisePackage';
			})
			?.modules?.find((module) => module.title === 'branchModule');
		return branchModule?.active;
	}, [modulesList]);

	return (
		<>
			<ChartHeader
				title={widgetName || 'orderDistribution'}
				subtitle={'dashboard.orderDistributioSubtitle'}
				showDivider={false}
			/>
			<S.TwoCardsInRow className="pb-3" wideCharts>
				<OrderDistributionWeekly />
				<OrderDistributionDaily />
			</S.TwoCardsInRow>
			<S.ThreeCardsInRow className="pt-3">
				<DistributionList
					fetchURL={REPORT.DASHBOARD.GET_TOP_DESTINATION_POINTS}
					title="topDestinations"
					filtered
					defaultDay="last30Days"
				/>
				<DistributionList
					title="deliveryCompDistribution"
					defaultDay="last30Days"
					filtered
					fetchURL={REPORT.DASHBOARD.GET_SHIPPING_PARTNERS_DISTRIBUTION}
					variant="with-logo"
				/>
				<DataListingCard
					fetchURL={REPORT.DASHBOARD.GET_DASHBOARD_WAREHOUSE_ORDERS}
					title={'warehouse'}
					defaultDay="last30Days"
				/>
				{isBranchModuleActive && (
					<DataListingCard
						title="branch"
						defaultDay="last30Days"
						fetchURL={REPORT.DASHBOARD.GET_DASHBOARD_BRANCH_ORDERS}
						variant="branch"
					/>
				)}
				<DistributionList
					title="saleChannelDistribution"
					defaultDay="last30Days"
					filtered
					fetchURL={REPORT.DASHBOARD.GET_SALES_CHANNEL_DISTRIBUTION}
				/>
				<DistributionList
					fetchURL={REPORT.ANALYSIS.GET_PAYMENT_METHOD}
					title="paymentMethod"
					filtered
					defaultDay="last30Days"
				/>
			</S.ThreeCardsInRow>
		</>
	);
};

export const OrderDistributionWeekly = ({ widgetName }) => {
	const { t } = useTranslation();
	const body = useMemo(
		() => ({
			from: dayjs().subtract(30, 'day').endOf('day').toISOString(),
			to: dayjs().endOf('day').toISOString()
		}),
		[]
	);

	const parseDataDaily = (data) => {
		const labels = data.map((item) => t(dayjs(item.xAxis).format('dddd').toLowerCase()));
		const datasets = [
			{
				label: t('orders'),
				data: data.map((item) => item.yAxis),
				backgroundColor: '#3D5FFF',
				borderColor: '#3D5FFF'
			}
		];
		return { labels, datasets };
	};

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_OVERVIEW_DAILY,
		body,
		parseData: parseDataDaily
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={widgetName || 'dashboard.orderDistributionWeekly'} />
			<BarChartV2 data={data} loading={loading} />
		</Card>
	);
};

export const OrderDistributionDaily = ({ widgetName }) => {
	const { t } = useTranslation();
	const body = useMemo(
		() => ({
			from: dayjs().subtract(30, 'day').endOf('day').toISOString(),
			to: dayjs().endOf('day').toISOString()
		}),
		[]
	);

	const parseDataHourly = (data) => {
		const labels = data.map((item) => t(dayjs(item.xAxis).format('HH A')));
		const datasets = [
			{
				label: t('orders'),
				data: data.map((item) => item.yAxis),
				backgroundColor: '#3D5FFF',
				borderColor: '#3D5FFF'
			}
		];
		return { labels, datasets };
	};

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.DASHBOARD.GET_DASHBOARD_OVERVIEW_HOURLY,
		body,
		parseData: parseDataHourly
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={widgetName || 'dashboard.orderDistributionDaily'} />
			<BarChartV2 data={data} loading={loading} />
		</Card>
	);
};

export default Dashboard;
